import { API_ERROR, DEAFULT_ERROR_MESSAGE } from '../constants/ErrorMessages';
import TEST_IMG from '../assets/img/jpg/speed-test-min.jpg';
import {
  DPJ_TEST_TYPE_SESSIONSTORAGE_KEY,
  NETWORK_DOWNLOAD_IMG_SIZE_IN_KB,
  OFFLINE_DURATION_LOCALSTORAGE_KEY,
  OFFLINE_LAST_START_TIME_LOCALSTORAGE_KEY,
  OFFLINE_TIMESTAMP_LOCALSTORAGE_KEY,
  TEST_START_TIME_SESSIONSTORAGE_KEY,
} from '../constants/Constants';
import {
  PROCTOR_TEST_START_TIME_LOCALSTORAGE_KEY,
  PROCTOR_IMAGE_SEQUENCE_DETAILS_LOCALSTORAGE_KEY,
  PROCTOR_FAILED_IMG_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_INITIATED_IMAGE_UPLOAD_LOCALSTORAGE_KEY,
  PROCTOR_SIGNED_URL_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY,
  PROCTOR_DROP_IMG_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_SUCCESS_IMG_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_WEBCAM_ERROR_COUNTER_LOCALSTORAGE_KEY,
  FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY,
  PROCTOR_SIGNED_URL_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_NEXT_SEQUENCE_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_URL_API_FAIL_COUNTER_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_URL_API_SUCCESS_COUNTER_LOCALSTORAGE_KEY,
  CAMERA_ACCESS_LOCALSTORAGE_KEY,
} from '../constants/Proctor';
import { REALTIME_ALERT_PARAMETERS_LOCALSTORAGE_KEY } from '../components/RealTimeProctoringFeedback/constant';
import {
  VAD_INCIDENT_COUNT_LOCALSTORAGE_KEY,
  VAD_SESSION_CAP_REMAINING_LOCALSTORAGE_KEY,
} from '../components/shared/VAD/constants';

export const getApiErrorData = (error, apiCode) => {
  const output = {};
  const input = API_ERROR[apiCode];
  if (input) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      output.status = error.response.status;
      if (input[error.response.status]) {
        output.message = input[error.response.status].message;
        output.apiErrorInfo = {
          ...input[error.response.status],
          errorCode: `${apiCode}:${error.response.status}`,
        };
      } else {
        output.message = DEAFULT_ERROR_MESSAGE.message;
        output.apiErrorInfo = {
          ...DEAFULT_ERROR_MESSAGE,
          errorCode: `${apiCode}:${error.response.status}`,
        };
      }
      const customErrorDescription = error.response.data?.description;
      if (
        error.response.data?.action?.display_custom_message &&
        customErrorDescription
      ) {
        output.apiErrorInfo = {
          ...output.apiErrorInfo,
          message: customErrorDescription,
          copyText: null,
        };
        output.message = customErrorDescription;
      }
    } else if (error.request) {
      if (error.code === 'ECONNABORTED') {
        output.message = input.network_error?.message;
        output.apiErrorInfo = {
          ...input.network_error,
          errorCode: `${apiCode}:TMT`,
        };
      } else {
        output.message = input.network_error?.message;
        output.apiErrorInfo = {
          ...input.network_error,
          errorCode: `${apiCode}:ENT`,
        };
      }
    }
  }
  return output;
};

// file size in byte
export const getDataInMbps = ({ startTime, endTime, filesize }) => {
  const duration = (endTime - startTime) / 1000;
  const bitsLoaded = filesize * 8;
  const speedMbps = (bitsLoaded / duration / 1024 / 1024).toFixed(4);
  return speedMbps;
};

export const getDownloadSpeedInMbps = async () => {
  try {
    const startTime = new Date();
    await fetch(`${TEST_IMG}?nntimestamp=${new Date().getTime()}`);
    const endTime = new Date();
    return getDataInMbps({
      startTime,
      endTime,
      filesize: NETWORK_DOWNLOAD_IMG_SIZE_IN_KB,
    });
  } catch (err) {
    return false;
  }
};

export const getDownloadSpeed = async () => {
  try {
    const downloadSpeedInMbps = await getDownloadSpeedInMbps();
    return `Download Speed: ${downloadSpeedInMbps} Mbps`;
  } catch (err) {
    return false;
  }
};

export const getUserBandwidth = async () => {
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;
  const downloadSpeed = connection
    ? connection.effectiveType
    : await getDownloadSpeed();
  return downloadSpeed;
};

export const getNetworkConnection = () =>
  navigator.connection ||
  navigator.mozConnection ||
  navigator.webkitConnection ||
  null;

export const getNetworkConnectionInfo = () => {
  const connection = getNetworkConnection();
  if (!connection) {
    return {};
  }
  return {
    rtt: connection.rtt,
    type: connection.type,
    saveData: connection.saveData,
    downLink: connection.downLink,
    downLinkMax: connection.downLinkMax,
    effectiveType: connection.effectiveType,
  };
};

export const updateInternetSpeedInfo = ({ uploadSpeed }) => {
  // storing data in session storage
  let imageNetworkSpeed = sessionStorage.getItem('image-internet-speed');
  imageNetworkSpeed = imageNetworkSpeed ? JSON.parse(imageNetworkSpeed) : {};
  imageNetworkSpeed.uploadSpeed = uploadSpeed;
  sessionStorage.setItem(
    'image-internet-speed',
    JSON.stringify(imageNetworkSpeed)
  );
};

export const incrementProctorCacheBasedOnKey = (
  key,
  storage = localStorage
) => {
  // storing data in session storage
  let keyData = storage?.getItem(key);
  keyData = keyData || 0;
  keyData = +keyData + 1;
  storage?.setItem(key, keyData);
  return keyData; // Return the new value
};

export const getProctorCacheValue = (
  key,
  storage = localStorage,
  defaultValue = 0
) => {
  try {
    const stored = storage?.getItem(key);
    return stored ? parseInt(stored, 10) : defaultValue;
  } catch (error) {
    console.warn(
      `Failed to load value from ${
        storage === localStorage ? 'localStorage' : 'sessionStorage'
      }:`,
      error
    );
    return defaultValue;
  }
};

export const setProctorTestStartTimeInCache = startTime => {
  const proctorStartTime = localStorage.getItem(
    PROCTOR_TEST_START_TIME_LOCALSTORAGE_KEY
  );
  if (!proctorStartTime) {
    localStorage.setItem(PROCTOR_TEST_START_TIME_LOCALSTORAGE_KEY, startTime);
  }
};

// Special keys that need conditional removal
const specialKeys = [
  PROCTOR_SIGNED_URL_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_NEXT_SEQUENCE_LOCALSTORAGE_KEY,
  PROCTOR_BATCHED_SIGNED_URL_API_SUCCESS_COUNTER_LOCALSTORAGE_KEY,
];

const handleSpecialKeys = (preserveKey = null) => {
  const signedUrlData = localStorage.getItem(
    PROCTOR_SIGNED_URL_LOCALSTORAGE_KEY
  );
  if (!signedUrlData) {
    // Remove special keys if no signed URL data exists
    specialKeys.forEach(key => {
      if (key !== preserveKey) {
        localStorage.removeItem(key);
      }
    });
    return;
  }

  try {
    const parsedData = JSON.parse(signedUrlData);
    const keys = Object.keys(parsedData);

    // Check if we have exactly 6 keys and they are all between 1-6
    const hasValidSequence =
      keys.length === 6 &&
      keys.every(key => {
        const numKey = parseInt(key, 10);
        return numKey >= 1 && numKey <= 6;
      });

    // Remove special keys if sequence is invalid
    if (!hasValidSequence) {
      specialKeys.forEach(key => {
        if (key !== preserveKey) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    specialKeys.forEach(key => {
      if (key !== preserveKey) {
        localStorage.removeItem(key);
      }
    });
  }
};

export const removingProctoringSessionData = (
  initiaCall,
  preserveKey = null
) => {
  // Common keys to remove from localStorage
  const localStorageKeys = [
    FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY,
    OFFLINE_DURATION_LOCALSTORAGE_KEY,
    OFFLINE_LAST_START_TIME_LOCALSTORAGE_KEY,
    OFFLINE_TIMESTAMP_LOCALSTORAGE_KEY,
    PROCTOR_FAILED_IMG_COUNTER_LOCALSTORAGE_KEY,
    PROCTOR_TEST_START_TIME_LOCALSTORAGE_KEY,
    PROCTOR_INITIATED_IMAGE_UPLOAD_LOCALSTORAGE_KEY,
    PROCTOR_SIGNED_URL_COUNTER_LOCALSTORAGE_KEY,
    PROCTOR_IMAGE_SEQUENCE_DETAILS_LOCALSTORAGE_KEY,
    PROCTOR_DROP_IMG_COUNTER_LOCALSTORAGE_KEY,
    PROCTOR_SUCCESS_IMG_COUNTER_LOCALSTORAGE_KEY,
    PROCTOR_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY,
    PROCTOR_WEBCAM_ERROR_COUNTER_LOCALSTORAGE_KEY,
    REALTIME_ALERT_PARAMETERS_LOCALSTORAGE_KEY,
    PROCTOR_BATCHED_SIGNED_URL_FAIL_COUNTER_LOCALSTORAGE_KEY,
    PROCTOR_BATCHED_SIGNED_URL_API_FAIL_COUNTER_LOCALSTORAGE_KEY,
    CAMERA_ACCESS_LOCALSTORAGE_KEY,
    VAD_INCIDENT_COUNT_LOCALSTORAGE_KEY,
    VAD_SESSION_CAP_REMAINING_LOCALSTORAGE_KEY,
  ];

  // Common keys to remove from sessionStorage
  const sessionStorageKeys = [
    PROCTOR_NEXT_IMAGE_SEQUENCE_LOCALSTORAGE_KEY,
    TEST_START_TIME_SESSIONSTORAGE_KEY,
    DPJ_TEST_TYPE_SESSIONSTORAGE_KEY,
  ];

  // Remove all common keys except preserveKey
  localStorageKeys.forEach(key => {
    if (key !== preserveKey) {
      localStorage.removeItem(key);
    }
  });
  sessionStorageKeys.forEach(key => {
    if (key !== preserveKey) {
      sessionStorage.removeItem(key);
    }
  });

  if (initiaCall) {
    handleSpecialKeys(preserveKey);
  } else {
    // Remove special keys for non-initial call
    specialKeys.forEach(key => {
      if (key !== preserveKey) {
        localStorage.removeItem(key);
      }
    });
  }
};

export function clearLocalStorageExcept(preserveKey = null) {
  const preserved = {};

  // Backup special keys to preserve
  specialKeys.forEach(key => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      preserved[key] = value;
    }
  });

  // Clear all
  localStorage.clear();

  // Restore preserved keys
  Object.entries(preserved).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });

  // Handle special keys validation
  handleSpecialKeys(preserveKey);
}

export const setProctoringImageStatusData = ({
  imageSequence,
  status,
  retry = 0,
  reason,
  subReason,
}) => {
  let proctorImageSequenceData = localStorage.getItem(
    PROCTOR_IMAGE_SEQUENCE_DETAILS_LOCALSTORAGE_KEY
  );
  proctorImageSequenceData = proctorImageSequenceData
    ? JSON.parse(proctorImageSequenceData)
    : {};
  proctorImageSequenceData[imageSequence] = {
    status,
    retry,
    reason,
    subReason,
  };
  localStorage.setItem(
    PROCTOR_IMAGE_SEQUENCE_DETAILS_LOCALSTORAGE_KEY,
    JSON.stringify(proctorImageSequenceData)
  );
};

export const getTestCasesFromJuit = (xmlData, values) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlData, 'text/xml');
  const testCases = xmlDoc.getElementsByTagName('testcase');
  const testCasesArray = [];

  for (let i = 0; i < testCases.length; i += 1) {
    const testCase = testCases[i];
    const testCaseObj = {
      ...values,
      name: testCase.getAttribute('name'),
      classname: testCase.getAttribute('classname'),
      time: parseFloat(testCase.getAttribute('time')),
      status: '',
    };

    const failure = testCase.getElementsByTagName('failure')[0];
    const error = testCase.getElementsByTagName('error')[0];
    const skipped = testCase.getElementsByTagName('skipped')[0];

    testCaseObj.status = failure || error || skipped ? 'NC' : 'AC/OK';

    testCasesArray.push(testCaseObj);
  }
  return testCasesArray;
};

export const getFinalTestCasesForPodBased = (initial, current) => {
  if (initial.length === 0) {
    return current;
  }
  const result = initial?.map(testcase => {
    const currentData = testcase;
    const testcaseInCurrent = current?.find(
      data =>
        data.name === testcase.name && data.framework === testcase.framework
    );
    if (testcaseInCurrent) {
      currentData.status = testcaseInCurrent.status;
    }
    return currentData;
  });
  return result;
};

export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export default {
  getApiErrorData,
  getUserBandwidth,
  getDownloadSpeed,
  getDownloadSpeedInMbps,
  getDataInMbps,
  getNetworkConnection,
  getNetworkConnectionInfo,
  getTestCasesFromJuit,
  clearLocalStorageExcept,
  sleep,
};
